/**
 * Client-side resolution of short link references (`addylink:N`).
 *
 * The Ask Addy backend interns long signed URLs from tool results as short
 * references so the model can stream responses without pausing to generate
 * 500+ character URLs. The real URLs arrive out-of-band via the SSE `links`
 * event as a map of reference -> URL.
 *
 * Mirrors backend/functions/mcp-client/src/linkReferences.ts (resolve side).
 */

export const LINK_REF_SCHEME = "addylink:";

const MARKDOWN_REF_LINK_PATTERN = /\[([^\]]*)\]\(<?addylink:(\d+)>?\)/g;
const BARE_REF_PATTERN = /addylink:(\d+)/g;

/** Longest label we will render for a derived document link. */
const MAX_DERIVED_LABEL_LENGTH = 60;
/** Storage objects are keyed with an upload id or UUID; strip it for display. */
const STORAGE_KEY_PREFIX_PATTERN =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d{10,}(?:-[0-9a-z]+)?)-/i;

/**
 * Build a readable label for a URL that has to be rendered as a link.
 *
 * A signed storage URL is ~900 characters. Rendered as its own link text it
 * fills the answer with query string, so recover the object's filename and show
 * that instead. Mirrors deriveLinkLabelFromUrl in the backend resolver.
 *
 * @param {string} url
 * @returns {string} a short human-readable label, never empty
 */
export function deriveLinkLabelFromUrl(url) {
  const fallback = "Document";
  if (!url || typeof url !== "string") return fallback;

  let pathname;
  try {
    pathname = new URL(url, "https://placeholder.invalid").pathname;
  } catch (error) {
    return fallback;
  }

  const lastSegment = pathname.split("/").filter(Boolean).pop();
  if (!lastSegment) return fallback;

  let name = lastSegment;
  try {
    name = decodeURIComponent(lastSegment);
  } catch (error) {
    // Keep the raw segment when it is not valid percent-encoding.
  }

  name = name.replace(STORAGE_KEY_PREFIX_PATTERN, "").trim();
  if (!name) return fallback;

  return name.length > MAX_DERIVED_LABEL_LENGTH
    ? `${name.slice(0, MAX_DERIVED_LABEL_LENGTH - 3)}...`
    : name;
}

/**
 * Replaces `addylink:N` references with real URLs from `linkMap`.
 *
 * Unknown references in markdown links are reduced to their label text —
 * this doubles as the streaming placeholder: the document name renders as
 * plain text until the `links` event delivers the URL, then a re-render
 * upgrades it in place to a clickable link.
 *
 * @param {string} text - Message text possibly containing references
 * @param {Object<string, string>} linkMap - Map of "addylink:N" -> URL
 * @returns {string} Text with references resolved (or stripped to labels)
 */
export function resolveLinkReferences(text, linkMap) {
  if (!text || typeof text !== "string" || !text.includes(LINK_REF_SCHEME)) {
    return text;
  }

  const map = linkMap || {};

  // Markdown links first so angle-bracket wrapping is applied to the URL.
  let resolved = text.replace(
    MARKDOWN_REF_LINK_PATTERN,
    (match, label, index) => {
      const url = map[`${LINK_REF_SCHEME}${index}`];
      if (url) {
        return `[${label}](<${url}>)`;
      }

      return label;
    }
  );

  // Bare references outside markdown links. Returning the bare URL here is what
  // put a 900-character signed link into the visible text of an answer: the
  // markdown renderer autolinks it and the URL becomes its own label. Render a
  // proper link named after the document instead. Unknown refs still vanish.
  resolved = resolved.replace(BARE_REF_PATTERN, (match, index) => {
    const url = map[`${LINK_REF_SCHEME}${index}`];
    return url ? `[${deriveLinkLabelFromUrl(url)}](<${url}>)` : "";
  });

  return resolved;
}
